import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes.ts'

import { ToursHeader } from 'pages/tours/search-result-page/components'

import { getShareUrl } from 'entities/tour/ui/appeal-details/libs/getShareUrl.ts'
import { prepareImages } from 'entities/umrah/libs'
import {
  UmraPilgrimsCount,
  UmraTourDescription,
  UmrahFlight,
  UmrahInformation,
} from 'entities/umrah/ui/umrah-detail/components'
import { UmrahTourInfo } from 'entities/umrah/ui/umrah-tour-info'
import { setSettingForApp } from 'entities/user/module/app-settings'

import { UmrahTour } from 'shared/api/umrah/tours'
import { ShareKeys, WHATSAPP_NUMBER } from 'shared/constants'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList.ts'
import { checkUserAuth } from 'shared/lib/checkUserAuth.ts'
import { dateFormat } from 'shared/lib/dateFormat.ts'
import { openExternalUrl } from 'shared/lib/external-open'
import { getRedirectUrl } from 'shared/lib/getRedirectUrl'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { ImageCarousel } from 'shared/ui/image-carousel'
import { ShareButton } from 'shared/ui/share-button'

import s from './styles.module.scss'

interface Props {
  tour?: UmrahTour | null
  pilgrimsCount?: number
}

export const UmrahTourDetail = observer(({ tour, pilgrimsCount }: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const headerTitle = tour?.agency?.name
  const headerSubtitle = `${dateFormat(tour?.schedule?.start_date, DATE_FORMAT_LIST.FULL_DATE_SHORT_YEAR)}
  - ${dateFormat(tour?.schedule?.end_date, DATE_FORMAT_LIST.FULL_DATE_SHORT_YEAR)}`

  const btnTitle = !!pilgrimsCount
    ? 'umrah_block.write_whatsapp'
    : 'umrah_block.go_to_booking'

  const sortedRoutes = useMemo(() => {
    return tour?.routes
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => item.location)
  }, [tour])

  const preparedImages = useMemo(() => prepareImages(tour), [tour])

  const shareUrl = getShareUrl(
    RoutingPaths.umrah.tourDetail.path(
      tour?.id || '',
      tour?.schedule?.id || '',
    ),
  )

  const goToGallery = () => {
    navigate(RoutingPaths.umrah.gallery.path(), {
      state: {
        title: headerTitle,
        images: preparedImages,
      },
    })
  }

  const handleSubmit = async () => {
    if (!!pilgrimsCount) {
      openExternalUrl(`https://wa.me/${WHATSAPP_NUMBER}`)
    } else {
      const navigatePath = RoutingPaths.umrah.prepayment.path()

      const redirectUrl = getRedirectUrl(navigatePath)

      await checkUserAuth({
        redirectUrl,
        onSuccess: () => {
          navigate(navigatePath)
        },
        onError: () => {
          const payload = {
            tour: tour,
          }

          void setSettingForApp({
            key: ShareKeys.umrah,
            value: JSON.stringify(payload),
          })
        },
      })
    }
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  return (
    <div className={s.container}>
      <ToursHeader
        onGoBack={handleGoBack}
        title={headerTitle}
        subtitle={headerSubtitle}
        actionRight={
          <ShareButton
            shareUrl={shareUrl}
            text={t('umrah_block.share_title', {
              tour: headerTitle,
            })}
          />
        }
      />

      <ImageCarousel
        images={preparedImages}
        onClick={goToGallery}
      />

      <div className={s.box}>
        <div className={s.paper}>
          <UmrahTourInfo
            priceText={t('umrah_block.moment_price')}
            tour={tour}
          />
        </div>

        <UmrahFlight routes={sortedRoutes} />

        <UmraTourDescription text={tour?.description} />

        <UmrahInformation />

        {!!pilgrimsCount && <UmraPilgrimsCount pilgrimsCount={pilgrimsCount} />}
      </div>

      <ButtonContinue
        onClick={handleSubmit}
        className={s.button}
      >
        <span>{t(btnTitle)}</span>
      </ButtonContinue>
    </div>
  )
})
